/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeGetRequest, makePostRequest, makePutRequest } from "../../network/http-client"
import type { ApplicationTypeStore } from "./application-type-store"
import type {
    ApplicationCreateType,
    ApplicationType,
    ApplicationTypeCategory,
    ApplicationTypePlatform,
    ApplicationTypeSDK,
    ApplicationUpdateType
} from "./application-type-types"

enum Endpoint {
    applicationType = "/api/private/application-type"
}

export class ApplicationTypeService {
    constructor(
        private readonly applicationStore: ApplicationTypeStore,
        private readonly logger: LoggerStore
    ) {}

    // if no orgId is presented, the request returns all application types for the current logged user
    async getApplicationTypes(orgId: number | null = null): Promise<Result<ApplicationType[]>> {
        const params = orgId ? { organizationId: `${orgId}` } : null
        const appTypes = await makeGetRequest<ApplicationType[]>(Endpoint.applicationType, params)
        if (appTypes.success && appTypes.data) {
            this.applicationStore.setAllAppTypes(appTypes.data)
        }
        if (!appTypes.success) {
            this.logger.logApiError("Get application types failed.", appTypes.apiError, appTypes.error)
        }
        return appTypes
    }

    async getApplicationType(appTypeId: number): Promise<Result<ApplicationType>> {
        const appType = await makeGetRequest<ApplicationType>(`${Endpoint.applicationType}/${appTypeId}`, {})
        if (appType.success && appType.data) {
            this.applicationStore.setAppType(appType.data)
        }
        if (!appType.success) {
            this.logger.logApiError("Get application type failed.", appType.apiError, appType.error)
        }
        return appType
    }

    // Only the description is mutable; the other application type fields are fixed
    // when the application type is created.
    async updateApplicationType(appTypeId: number, description: string): Promise<Result<ApplicationType>> {
        const appType = await makePutRequest<ApplicationUpdateType, ApplicationType>(
            `${Endpoint.applicationType}/${appTypeId}`,
            {},
            { description }
        )
        if (appType.success && appType.data) {
            this.applicationStore.setAppType(appType.data)
            this.logger.logOk("Application type updated successfully.")
        }
        if (!appType.success) {
            this.logger.logApiError("Update application type failed", appType.apiError, appType.error)
        }
        return appType
    }

    async getSDK(appTypeId: number): Promise<Result<ApplicationTypeSDK>> {
        const sdkKeys = await makeGetRequest<ApplicationTypeSDK>(
            `${Endpoint.applicationType}/${appTypeId}/sdk-api-keys`,
            {}
        )
        if (!sdkKeys.success) {
            this.logger.logApiError("Get SDK keys failed", sdkKeys.apiError, sdkKeys.error)
        }
        return sdkKeys
    }

    async updateApplicationTypeIcon(appTypeId: number, iconFile: File | null): Promise<Result<void>> {
        if (!iconFile) {
            return {
                success: false,
                error: new Error("Icon File is empty")
            }
        }

        const formData = new FormData()
        formData.append("file", iconFile)
        const icon = await makePutRequest<FormData, void>(`${Endpoint.applicationType}/${appTypeId}/icon`, {}, formData)

        if (icon.success) {
            this.applicationStore.updateIconVersion(appTypeId)
            this.logger.logOk("Icon uploaded successfully.")
        }
        if (!icon.success) {
            this.logger.logApiError("Upload icon failed", icon.apiError, icon.error)
        }
        return icon
    }

    async createApplicationType(
        organizationId: number,
        description: string,
        sourcePackageName: string,
        platform: ApplicationTypePlatform,
        category: ApplicationTypeCategory
    ): Promise<Result<ApplicationType>> {
        const appType = await makePostRequest<ApplicationCreateType, ApplicationType>(
            Endpoint.applicationType,
            {},
            {
                organizationId,
                description,
                sourcePackageName,
                platform,
                category
            }
        )
        if (appType.success && appType.data) {
            this.logger.logOk("Application type created successfully.")
        }
        if (!appType.success) {
            this.logger.logApiError("Create application type failed", appType.apiError, appType.error)
        }
        return appType
    }
}
